import React from 'react';
import PropTypes from 'prop-types';
import {shape, number, string, func, bool, arrayOf, object} from 'prop-types';
import {Container, Grid, Image, Menu, Icon} from 'semantic-ui-react';
import classnames from 'classnames';
import merge from 'lodash/merge';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {EmptyFunc, isNoU, makeDBImageUrl} from "utils/DBUtils";
import CurrentImageCount from './CurrentImageCount';
import imageEditConfig from './imageFormConfig';
import CarouselArrow from './CarouselArrow';
import ResourceForm from "../forms/ResourceForm";
import {FormattedMessage} from "react-intl";
import components from 'components/forms/Component Sets/UnvalidatedBasicFormComponents';

const defaultCarouselSettings = {
  dots: false,
  infinite: false,
  focusOnSelect: true,

  centerMode: true,
  className: 'db-center-mode',
  centerPadding: '100px',

  speed: 100,
  slidesToShow: 1,
  slidesToScroll: 1,

  prevArrow: <CarouselArrow direction='left' />,
  nextArrow: <CarouselArrow direction='right' />
};

export default class ImageEditForm extends React.Component {
  static propTypes = {
    formData: shape({
      id: string.isRequired,
      filename: string,
      name: string,
      imageType: string.isRequired,
      altAttribute: string,
      source: string,
      link: string,
      height: number.isRequired,
      width: number.isRequired
    }),
    onSaveData: func.isRequired,
    enableBulkUpload: bool,
    pendingImages: arrayOf(object),
    selectedIndex:  number,
    carouselSettings: object,
    selectedImageSiblings: arrayOf(object),
    siblingClicked: func
  };

  static defaultProps = {
    onSaveData: EmptyFunc,
    pendingImages: [],
    selectedIndex: 0,
    carouselSettings: {},
    selectedImageSiblings: [],
    siblingClicked: EmptyFunc
  };

  constructor(props) {
    super(props);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.setSlider = this.setSlider.bind(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onKeyDown, true);
  }

  componentWillReceiveProps(nextProps) {
    const clickedSubmit = this.props.uploadingImage || this.props.editingImage;

    if (clickedSubmit && Object.keys(nextProps.errors).length === 0) {
      this.props.scrollContentToTop();
    }

    // advances the carousel after uploading an image.
    if (nextProps.selectedIndex !== this.props.selectedIndex) {
      this.slider.slickGoTo(nextProps.selectedIndex);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown, true);
  }

  onKeyDown(event) {
    if (event.key === 'Enter') {
      this.props.onSaveData(this.props.formData);
    }
  }

  setSlider(slider) {
    if (slider) {
      this.slider = slider;
    }
  }

  get imagePreviewUrl() {
    return this.props.formData.url || makeDBImageUrl(this.props.formData);
  }

  get carouselSettings() {

    // there are a couple of things happening here:
    //   - defaultCarouselSettings can be overridden via props.carouselSettings, including
    //     react-slick callbacks like afterChange, ref, etc.
    //   - this component uses at least one of those callbacks internally.
    //
    // to prevent this component's callbacks from getting clobbered, we override the
    // react-slick props we need last to ensure that, if a callback has been passed on
    // props, that caller's callback can also get called with the same arguments when we
    // invoke our own callback with them.

    const handleRef = (...args) => {
      // pass arguments to the caller's callback, if it exists.
      if (this.props.carouselSettings.ref) {
        this.props.carouselSettings.ref(...args);
      }

      // call this component's callback.
      this.setSlider(...args);
    };

    return merge({}, defaultCarouselSettings, this.props.carouselSettings, {
      ref: handleRef
    });
  }

  getSlideClassName = index => {
    // determines which slides are previous and next in relation to the current one.
    // we need to know this in order to style the slides to resemble the design mock.
    return classnames({
      'db-previous-slide': index === (this.props.selectedIndex - 1),
      'db-next-slide': index === (this.props.selectedIndex + 1)
    });
  };

  render() {
    return (
      <Grid divided="vertically" className="image-edit-form">
        <Grid.Row columns={2}>
          <Grid.Column>
            <a href={this.imagePreviewUrl} target="_blank">
              <Image src={this.imagePreviewUrl} fluid/>
            </a>
          </Grid.Column>
          <Grid.Column>
            <Container>
              <p className='word-wrap'>Image ID: {this.props.formData.id}</p>
              <p className='image-edit-dimensions'>
                <span>Pixel Height: {this.props.formData.height}</span>
                <span>Pixel Width: {this.props.formData.width}</span>
              </p>
              <p>Click image to view actual size.</p>
                {this.props.enableBulkUpload && (
                  <div className='bulk-image-carousel-container'>
                    <Slider {...this.carouselSettings}>
                      {this.props.pendingImages.map((pendingOriginalUpload, index) => (
                        <img
                          className={this.getSlideClassName(index)}
                          key={pendingOriginalUpload.uploadId}
                          src={pendingOriginalUpload.url} />
                      ))}
                    </Slider>
                    <span className='bulk-image-counter'>
                      Image <CurrentImageCount images={this.props.pendingImages} currentImage={this.props.formData} />
                    </span>
                  </div>
                )}
            </Container>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={1}>
          <Grid.Column>
            <ResourceForm
              resource={this.props.formData}
              config={imageEditConfig}
              components={components}
              errors={this.props.errors}
              onValueChanged={this.props.onValueChanged}
              onValidationError={this.props.onValidationError} />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={1}>
          <Grid.Column>
            {this.props.formData.imageType !== "original" && !isNoU(this.props.formData.originalImageId) ? (
              <div>
                <Menu.Item onClick={() => this.props.siblingClicked(this.props.formData.originalImageId)}>
                  <FormattedMessage id={'images.imageType.original'}/>&nbsp;
                </Menu.Item>
              </div>
            ) : (
              <div>
                <h5>Images using this original file:</h5>
                  {this.props.selectedImageSiblings.length > 0 ? this.props.selectedImageSiblings.map((image, index) => (
                    <Menu.Item onClick={() => this.props.siblingClicked(image.id)} key={image.id}>
                      <FormattedMessage id={`images.imageType.${image.imageType}`} />
                      {index < this.props.selectedImageSiblings.length - 1  ? ',' : ''}&nbsp;
                    </Menu.Item>
                  )) : (
                    <Menu.Item>
                      None&nbsp;
                    </Menu.Item>
                  )}
              </div>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
};



// WEBPACK FOOTER //
// ./public/components/images/ImageEditForm.jsx